/** Minimal Image-like handle so tests can mock `new Image()` without a DOM. */
export type CartModalImageHandle = {
    onload: (() => void) | null;
    onerror: (() => void) | null;
    src: string;
};

export type CartModalHiResCallbacks = {
    onSuccess: (hiResUrl: string) => void;
    onError: () => void;
};

/** Same replacement the cart modals have always used: first `_400_400_` only. */
export function toCartModalHiResUrl(imgFull: string): string {
    return imgFull.replace('_400_400_', '_1280_1280_');
}

/**
 * Prefetches the cart-modal hi-res URL. Only the latest `load` may call back;
 * `invalidate` drops in-flight work (unmount). Does not touch titles or modal IDs.
 */
export class CartModalHiResLoader {
    private generation = 0;
    private current: CartModalImageHandle | null = null;
    private readonly createImage: () => CartModalImageHandle;

    constructor(createImage?: () => CartModalImageHandle) {
        this.createImage = createImage ?? (() => new Image() as CartModalImageHandle);
    }

    load(imgFull: string, callbacks: CartModalHiResCallbacks): void {
        this.invalidate();
        const generation = this.generation;
        const hiResUrl = toCartModalHiResUrl(imgFull);
        const image = this.createImage();
        this.current = image;

        image.onload = () => {
            if (!this.isCurrent(generation, image)) {
                return;
            }
            callbacks.onSuccess(hiResUrl);
        };
        image.onerror = () => {
            if (!this.isCurrent(generation, image)) {
                return;
            }
            callbacks.onError();
        };
        image.src = hiResUrl;
    }

    invalidate(): void {
        this.generation += 1;
        this.detach();
    }

    private isCurrent(generation: number, image: CartModalImageHandle): boolean {
        return generation === this.generation && this.current === image;
    }

    private detach(): void {
        if (!this.current) {
            return;
        }
        this.current.onload = null;
        this.current.onerror = null;
        this.current = null;
    }
}
